import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { getSensorData, getUserDevices, reset, changeCurrentDevice, resetOnCurrentDeviceChange } from '../features/sensors/sensorSlice';
import { updateOutputState } from '../features/outputState/outputStateSlice';
import Rules from '../components/Rules';
import { Container, Row, Col, Button, Dropdown, DropdownButton, Tab, Tabs, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import toastConfig from '../app/toast-config';
import Chart from '../components/Chart';
import GaugeChart from 'react-gauge-chart';
import Websockets from '../components/Websockets';

function Dashboard() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const clientId = localStorage.getItem('clientId');
  const { devices, currentDevice, sensorData, isUserDevicesLoading, isError, message, connectionSuccessMessage,
    temperature, humidity, soil_moisture, light_intensity, water_level } = useSelector((state) => state.deviceData);
  const { rules } = useSelector((state) => state.rulesData);
  const sensorDataMemo = useMemo(() => sensorData, [sensorData]);
  const waterLevelMemo = useMemo(() => water_level, [water_level]);
  const currentDeviceRef = useRef(null);
  const outputStatesData = useSelector((state) => state.outputStateData);
  const [ client, setClient ] = useState(undefined);
  const [ isClientSet, setIsClientSet ] = useState(false);
  const [ wsConnected, setWsConnected ] = useState(false);
  const [ isManualControlEnabled, setIsManualControlEnabled ] = useState(false);
  const [ controlMode, setControlMode ] = useState("");
  const [ temperatureData, setTemperatureData ] = useState(undefined);
  const [ humidityData, setHumidityData ] = useState(undefined);
  const [ soilMoistureData, setSoilMoistureData ] = useState(undefined);
  const [ lightIntensityData, setLightIntensityData ] = useState(undefined);
  const [ waterLevelData, setWaterLevelData ] = useState(undefined);
  const waterLevelWarning = 10; // turn on water level warning if water level in reservoir is under 10%
  const [ waterLevelLow, setWaterLevelLow ] = useState(false);
  const [ temperatureTimestamps, setTemperatureTimestamps ] = useState(undefined);
  const [ humidityTimestamps, setHumidityTimestamps ] = useState(undefined);
  const [ soilMoistureTimestamps, setSoilMoistureTimestamps ] = useState(undefined);
  const [ lightIntensityTimestamps, setLightIntensityTimestamps ] = useState(undefined);
  const [ waterLevelTimestamps, setWaterLevelTimestamps ] = useState(undefined);
  const [ selectedChart, setSelectedChart ] = useState("temperature");
  const [ chartDataTimeframe, setChartDataTimeframe ] = useState("Last 12h");
  const chartDataTimeframeRef = useRef("Last 12h");
  const [ outputStates, setOutputStates ] = useState({
    lights: 0,
    heating: 0,
    fan: 0,
    water_pump: 0
  });

  useEffect(() => {
    if(user) {
      dispatch(getUserDevices());
      dispatch(reset());
    }

    return () => {
      if(client) client.close();
    }
  }, [client, dispatch, user])

  useEffect(() => {
    if(isError && message && message !== "") {
      console.log(message);
      toast.error(message, toastConfig);
    }
  }, [isError, message]);

  useEffect(() => {
    if(outputStatesData.isError && outputStatesData.message && outputStatesData.message !== "") {
      console.log(outputStatesData.message);
      toast.error(outputStatesData.message, toastConfig);
    }
  }, [outputStatesData.isError, outputStatesData.message]);

  useEffect(() => {
    if(connectionSuccessMessage !== "") toast.success(connectionSuccessMessage, toastConfig);
  }, [connectionSuccessMessage]);

  useEffect(() => {
    if(user) { 
      if(currentDevice && currentDevice !== null) {
        if(currentDevice.id && currentDevice.id !== "") {
          const data = {
            deviceId: currentDevice.id,
            timeframe: chartDataTimeframe
          }
          console.log("getSensorData triggered on current device change.");
          dispatch(getSensorData(data));

          if(client) {
            // DODAJ READYSTATE PROVERU
            if(clientId) {
              let json = {
                action: "update_current_device",
                id: clientId,
                currentDevice: currentDevice.id
              }
              client.send(JSON.stringify(json));
              json = {
                action: "get_output_states",
                user: user._id,
                deviceId: currentDevice.id
              }
              console.log(`Getting output states for ${json.deviceId} after current device change...`);
              client.send(JSON.stringify(json));
            }
          }
        }
      }
    }
  }, [chartDataTimeframe, client, clientId, currentDevice, dispatch, user])

  useEffect(() => {
    console.log("outputStates updated.");
  }, [outputStates])

  useEffect(() => {
    if(currentDevice && currentDevice !== null) {
      if(currentDevice.id && currentDevice.id !== "") {
        const data = {
          deviceId: currentDevice.id,
          timeframe: chartDataTimeframe
        }
        dispatch(getSensorData(data))
      }
    }
  }, [chartDataTimeframe, currentDevice, dispatch])

  useEffect(() => {
    const filterData = (data, dataType, dataTimeFrame, treshold) => {
      var values = undefined;
      var timestamps = undefined;
      var temp;
      if(dataTimeFrame === "Week" || dataTimeFrame === "Month") {
        let diffTotal = 0;
        let lastAddedValue = undefined;
        temp = data.map((item, index) => {
          // if(item[dataType] >= 0 && item[dataType] !== null) {
          if(item[dataType] !== null) {
            if(index>0 && index < (data.length - 1)) {
              if(!lastAddedValue & item[dataType] && item[dataType] !== null) {
                lastAddedValue = item[dataType];
                // console.log(`No ${dataType} value in first array item - added ${dataType} - ${index} (${lastAddedValue})`);
                return { [dataType]: item[dataType], createdAt: item.createdAt };
              }
              var diff = item[dataType] - lastAddedValue;
              if(Math.abs(diff) >= treshold) {
                lastAddedValue = item[dataType];
                return { [dataType]: item[dataType], createdAt: item.createdAt };
              }
              if(diff > 0) diffTotal += diff
                else diffTotal -= diff;
              if(Math.abs(diffTotal) >= treshold) {
                diffTotal = 0;
                lastAddedValue = item[dataType];
                return { [dataType]: item[dataType], createdAt: item.createdAt };
              } 
              return undefined;
            }
            else {
              if(item[dataType] && item[dataType] !== null) {
                lastAddedValue = item[dataType];
                // console.log(`Added ${dataType} - ${index} (${lastAddedValue})`);
                return { [dataType]: item[dataType], createdAt: item.createdAt };
              } 
            }
          }
          return null;
        }).filter(item => item && item !== null);
        values = temp.map(item => item[dataType]);
        timestamps = temp.map(item => item.createdAt);
      }
      else {
        // remove this check when real water level sensor is added, this is only needed because of 0 values from the sensor used for testing
        if(dataType === "water_level" || dataType === "soil_moisture") {
          temp = data.map(item => item[dataType] >= 0 ? item[dataType] : null);
          values = temp.filter(item => item >=0 && item !== null);
          temp = data.map(item => item[dataType] >= 0 ? item.createdAt : null);
          timestamps = temp.filter(item => item && item !== null);
        }
        else {
          temp = data.map(item => item[dataType] ? item[dataType] : null);
          values = temp.filter(item => item && item !== null);
          temp = data.map(item => item[dataType] ? item.createdAt : null);
          timestamps = temp.filter(item => item && item !== null);
        }
      }
      setNewValues(values, timestamps, dataType);
      console.log(`Showing ${values.length} values for ${dataType}.`);
    };
    
    filterData(sensorData, "temperature", chartDataTimeframe, 0.5);
    filterData(sensorData, "humidity", chartDataTimeframe, 1);
    filterData(sensorData, "light_intensity", chartDataTimeframe, 20);
    filterData(sensorData, "soil_moisture", chartDataTimeframe, 10);
    filterData(sensorData, "water_level", chartDataTimeframe, 1);
    if(sensorData[sensorData.length - 1] && sensorData[sensorData.length - 1].water_level && sensorData[sensorData.length - 1].water_level < waterLevelWarning)
      setWaterLevelLow(true);
    else
      setWaterLevelLow(false);
  }, [chartDataTimeframe, sensorData])

  useEffect(() => {
    if(waterLevelLow) {
      toast.error(`Water level under ${waterLevelWarning}%, please fill the reservoir!`, toastConfig);
    }
    setWaterLevelLow(false);
  }, [waterLevelLow])

  useEffect(() => {
    if(controlMode === "auto") setIsManualControlEnabled(false);
    else if(controlMode === "manual") setIsManualControlEnabled(true);
  }, [controlMode])

  useEffect(() => {
    chartDataTimeframeRef.current = chartDataTimeframe;
    currentDeviceRef.current = currentDevice;
  })

  const setNewValues = (values, timestamps, dataType) => {
    switch (dataType) {
      case "temperature":
        setTemperatureData(values);
        setTemperatureTimestamps(timestamps);
        break;
      case "humidity":
        setHumidityData(values);
        setHumidityTimestamps(timestamps);
        break;
      case "water_level":
        setWaterLevelData(values);
        setWaterLevelTimestamps(timestamps);
        break;
      case "soil_moisture":
        setSoilMoistureData(values);
        setSoilMoistureTimestamps(timestamps);
        break;
      case "light_intensity":
        setLightIntensityData(values);
        setLightIntensityTimestamps(timestamps);
        break;
      default:
        break;
    }
  }

  const updateOutput = (output) => {
    if(wsConnected) {
      const data = {
        deviceId: currentDevice.id,
        data: {
          action: "update_output",
          output: output,
          state: outputStates[output] === 1 ? 0 : 1
        }
      }
      console.log(`Trying to update ${data.data.output} on device ${data.deviceId} to ${data.data.state}.`);
      dispatch(updateOutputState(data));
    }
    else {
      toast.error(`Can't change ${output} state - not connected to the server's websocket. Please refresh.`, toastConfig);
    }
  }

  const switchControlMode = () => {
    if(wsConnected) {
      let mode;
      if(controlMode === "auto") mode = "manual"
      else if(controlMode === "manual") mode = "auto";
      
      if(controlMode === "manual" && mode === "auto" && rules.length === 0) {
        toast.info(`Can't switch to auto: ${currentDevice.name} has no automation rules.`, toastConfig);
      }
      else {
        if(client && currentDevice != null) {
          var json = {
            action: "set_control_mode",
            userId: user._id,
            deviceId: currentDevice.id,
            mode: mode
          }
          console.log(`Setting control mode for ${json.deviceId} to ${mode}`);
          client.send(JSON.stringify(json));
        }
      }
    }
    else {
      toast.error(`Can't change ${currentDevice.name} control mode - not connected to the server's websocket. Please refresh.`, toastConfig);
    }
  }

  const handleChart = (e) => {
    setSelectedChart(e);
  }

  const handleChartTimeframe = (e) => {
    setChartDataTimeframe(e);
  }
  
  const handleCurrentDevice = async (e) => {
    const data = JSON.parse(e);
    await dispatch(resetOnCurrentDeviceChange());
    dispatch(changeCurrentDevice(data));
  }

  const getRealTimeReadings = () => {
    if(clientId) {
      var json = {
        action: "get_realtime_readings",
        deviceId: currentDevice.id
      }
      console.log(`Getting real timne sensor readings for ${json.deviceId}`);
      client.send(JSON.stringify(json));
    }
  }

  return (
    <div>
      {user ? (<>
        {isUserDevicesLoading ? (<>
          <Container className="mx-auto text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Container>
        </>) : (<>
          {currentDevice && currentDevice !== null ? (<>
            {!isClientSet ? (<>
              <Websockets
                setOutputStates={setOutputStates}
                setWsConnected={setWsConnected}
                setIsManualControlEnabled={setIsManualControlEnabled}
                setClient={setClient}
                setIsClientSet={setIsClientSet}
                setControlMode={setControlMode}
                setWaterLevelLow={setWaterLevelLow}
              />
            </>) : null}
            <Container className="dashboard justify-content-center">
                <Row className="row align-items-stretch">
                  <Col lg={7} md={12} className="popout mb-4 px-4 pb-4 pt-2 rounded mx-auto text-center">
                    {devices.length > 0 ? (<>
                      <Container className="dropdown-container px-0 mx-0 mb-1 d-flex justify-content-left">
                        <DropdownButton variant="success" title={currentDevice.name} onSelect={handleCurrentDevice} className="mr-2">
                          {
                            devices.map(device => (
                              <Dropdown.Item key={device._id} eventKey={`{ "id": "${device._id}", "name": "${device.name}" }`}>{device.name}</Dropdown.Item>
                            ))
                          }
                        </DropdownButton>
                        <DropdownButton variant="success" title={chartDataTimeframe} onSelect={handleChartTimeframe}>
                          <Dropdown.Item key="12h" eventKey="Last 12h">Last 12h</Dropdown.Item>
                          <Dropdown.Item key="24h" eventKey="Last 24h">Last 24h</Dropdown.Item>
                          <Dropdown.Item key="w" eventKey="Week">Week</Dropdown.Item>
                          <Dropdown.Item key="m" eventKey="Month">Month</Dropdown.Item>
                        </DropdownButton>
                        <Button variant="secondary" onClick={getRealTimeReadings} className="mx-3">Get readings</Button>
                      </Container>
                    </>) : null}
                    <Row className="justify-content-md-center align-items-start my-3">
                      <Col md={6}>
                        <Container className="pt-3 text-left">
                          {temperature !== "" ? (<p className="lead mx-auto my-0">Temperature: {temperature} ℃</p>) : null}
                          {humidity !== "" ? (<p className="lead mx-auto my-0">Humidity: {humidity} %</p>) : null}
                          {light_intensity !== "" ? (<p className="lead mx-auto my-0">Light intensity: {light_intensity} lux</p>) : null}
                          {soil_moisture !== "" ? (<p className="lead mx-auto my-0">Soil moisture: {soil_moisture} %</p>) : null}
                          
                          {sensorData && sensorData.length > 0 && sensorData[sensorData.length - 1].temperature !== undefined && temperature === "" ? (
                            <p className="lead mx-auto my-0">Temperature: {sensorData[sensorData.length - 1].temperature} ℃</p>
                          ) : null}
                          {sensorData && sensorData.length > 0 && sensorData[sensorData.length - 1].humidity !== undefined && humidity === "" ? (
                            <p className="lead mx-auto my-0">Humidity: {sensorData[sensorData.length - 1].humidity} %</p>
                          ) : null}
                          {sensorData && sensorData.length > 0 && sensorData[sensorData.length - 1].light_intensity !== undefined && light_intensity === "" ? (
                            <p className="lead mx-auto my-0">Light intensity:  {sensorData[sensorData.length - 1].light_intensity} lux</p>
                          ) : null}
                          {sensorData && sensorData.length > 0 && sensorData[sensorData.length - 1].soil_moisture !== undefined && soil_moisture === "" ? (
                            <p className="lead mx-auto my-0">Soil moisture:  {sensorData[sensorData.length - 1].soil_moisture} %</p>
                          ) : null}
                        </Container>
                      </Col>
                      <Col md={6} >
                        {water_level !== "" ? (<> 
                          <GaugeChart id="waterLevelGauge" percent={waterLevelMemo / 100} colors={['#EA4228', '#F5CD19', '#5BE12C']} nrOfLevels={20} className="waterLevelGauge"/>
                          <p className="lead fs-6 mx-auto">Reservoir water level</p>
                        </>) : null}
                        {sensorData && sensorData.length > 0 && sensorData[sensorData.length - 1].water_level !== undefined && water_level === "" ? (<> 
                          <GaugeChart id="waterLevelGauge" percent={sensorDataMemo[sensorDataMemo.length - 1].water_level / 100} colors={['#EA4228', '#F5CD19', '#5BE12C']} nrOfLevels={20} className="waterLevelGauge"/>
                          <p className="lead fs-6 mx-auto">Reservoir water level</p>
                        </>) : null}
                      </Col>
                    </Row>
                    <Tabs defaultActiveKey="temperature" id="uncontrolled-tab-example" className="mb-3 custom-tabs" onSelect={handleChart}>
                      <Tab eventKey="temperature" title="Temperature">
                        { selectedChart === "temperature" && temperatureData && temperatureData.length > 0 ? (<>
                          <Chart selectedChart={selectedChart} sensorData={temperatureData} timestamps={temperatureTimestamps}/>
                        </>) : null}
                      </Tab>
                      <Tab eventKey="humidity" title="Humidity">
                        { selectedChart === "humidity" ? (<>
                          <Chart selectedChart={selectedChart} sensorData={humidityData} timestamps={humidityTimestamps}/>
                        </>) : null}
                      </Tab>
                      <Tab eventKey="soil_moisture" title="Soil moisture">
                        { selectedChart === "soil_moisture" ? (<>
                          <Chart selectedChart={selectedChart} sensorData={soilMoistureData} timestamps={soilMoistureTimestamps}/>
                        </>) : null}
                      </Tab>
                      <Tab eventKey="light_intensity" title="Light intensity">
                        { selectedChart === "light_intensity" ? (<>
                          <Chart selectedChart={selectedChart} sensorData={lightIntensityData} timestamps={lightIntensityTimestamps}/>
                        </>) : null}
                      </Tab>
                      <Tab eventKey="water_level" title="Water level">
                        { selectedChart === "water_level" ? (<>
                          <Chart selectedChart={selectedChart} sensorData={waterLevelData} timestamps={waterLevelTimestamps}/>
                        </>) : null}
                      </Tab>
                    </Tabs>
                  </Col>

                  <Col lg="auto" md={12} className="mb-4 px-4 py-4 ms-auto rounded text-center mx-auto popout">
                    <Container className="d-flex align-items-center justify-content-center ml-n1">
                      <p className="lead fs-5 mr-1 my-0" style={ isManualControlEnabled ? { color:"#198754", fontWeight: "bold", width: "80px"} : {color : '#fff', fontWeight: "normal", width: "80px"} }>Manual</p>
                      <label className="switch">
                        <input type="checkbox" onChange={() => switchControlMode()} checked={!isManualControlEnabled}/>
                        <span className="slider mode-control"></span>
                      </label>
                      <p className="lead fs-5 my-0" style={ !isManualControlEnabled ? { color:"#198754", fontWeight: "bold", width: "70px"} : {color : '#fff', fontWeight: "normal", width: "70px"} }>Auto</p>
                    </Container>
                    <hr/>
                    <h4 className="lead fw-normal">Lights - Status: {outputStates.lights === 1 ? "ON" : "OFF"}</h4>
                    <label className="switch">
                      <input type="checkbox" onChange={() => updateOutput("lights")} checked={outputStates.lights === 1 ? true : false} disabled={!isManualControlEnabled}/>
                      <span className="slider"></span>
                    </label>
                    <hr/>
                    {/* <h4 className="lead fw-normal">Heating - Status: {outputStates.heating === 1 ? "ON" : "OFF"}</h4>
                    <label className="switch">
                      <input type="checkbox" onChange={() => updateOutput("heating")} checked={outputStates.heating === 1 ? true : false} disabled={!isManualControlEnabled}/>
                      <span className="slider"></span>
                    </label>
                    <hr/> */}
                    <h4 className="lead fw-normal">Fan - Status: {outputStates.fan === 1 ? "ON" : "OFF"}</h4>
                    <label className="switch">
                      <input type="checkbox" onChange={() => updateOutput("fan")} checked={outputStates.fan === 1 ? true : false} disabled={!isManualControlEnabled}/>
                      <span className="slider"></span>
                    </label>
                    <hr/>
                    <h4 className="lead fw-normal">Water pump - Status: {outputStates.water_pump === 1 ? "ON" : "OFF"}</h4>
                    <label className="switch">
                      <input type="checkbox" onChange={() => updateOutput("water_pump")} checked={outputStates.water_pump === 1 ? true : false} disabled={!isManualControlEnabled}/>
                      <span className="slider"></span>
                    </label>
                    <hr/>
                    {isClientSet ? (<Rules client={client}/>) : null}
                  </Col>
              </Row>
          </Container>
         </>) : (<>
            <h2 className="mx-auto text-center">You don't have any devices yet.</h2>
          </>)}
        </>)
      }</>) : (<>
        <Container className="mx-auto text-center">
          <h2>Please <Link to='/login'>login</Link> in order to access the dashboard.</h2>
        </Container>
      </>)}
    </div>
  )
}

export default Dashboard